import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBasket, Package, CheckCircle2, RotateCcw,
  ArrowRight, Star, Clock, Search,
  Home, Tag, ChevronRight, User, Eye, Ban, LifeBuoy,
  Truck, PackageCheck, ReceiptText, CreditCard, Sprout,
  PartyPopper, MapPin, Leaf, Flame, WheatOff,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../../../redux/store";
import {
  listenToFoodGuestOrders,
  markFoodItemsDelivered,
  FoodOrder,
} from "../../../../redux/foodOrderSlice";
import { listenToPublishedFoods } from "../../../../redux/foodSlice";

/* ═══════════════════════════════════════════════
   FRESH TOKENS — the StoreBuyerOrders slab system
   (hard borders, stacked shadows, corner domes)
   wearing the market-stall palette. Same shapes as
   the store page so the two never drift apart.
═══════════════════════════════════════════════ */
const F = {
  bg: "#FFF8F1",
  panelBg: "#FFFFFF",
  dark: "#2B1A12",
  textMuted: "#9C8A7E",
  textFaint: "#C9A98E",
  orange: "#FF7A1A",
  orangeDark: "#E06A00",
  orangeSoft: "#FFEFDF",
  red: "#FF3D57",
  redDark: "#D62741",
  redSoft: "#FFE7EA",
  border: "#F3E0CB",
  divider: "#F6E9DA",
  green: "#1FAA59",
  greenDark: "#17853F",
  greenSoft: "#E4F6EC",
};

const GRAD_RED = `linear-gradient(90deg, ${F.orange} 0%, ${F.red} 100%)`;
/* the fresh sweep — ink → sun → orange → red → sun → ink */
const SWEEP = `linear-gradient(90deg, ${F.dark}, #FFB347, ${F.orange}, ${F.red}, #FFB347, ${F.dark})`;

/* support lives on the same route the rest of the app points at */
const supportRoute = (userId?: string) => `/CustomerSupport/${userId ?? ""}`;

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-thumb{background:${F.border};border-radius:99px;}
  input,button{font-family:inherit;cursor:pointer;}
  input::placeholder{color:${F.textFaint};}
  :focus-visible{outline:2px solid ${F.orange};outline-offset:3px;border-radius:8px;}

  @keyframes fbSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes fbRise{from{opacity:0;transform:translateY(14px) scale(.98)}to{opacity:1;transform:none}}
  @keyframes fbBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
  @keyframes fbWiggle{0%,100%{transform:rotate(-3deg)}50%{transform:rotate(3deg)}}
  @keyframes fbDash{to{stroke-dashoffset:-28}}
  @keyframes fbPulse{0%,100%{box-shadow:0 0 0 0 rgba(255,61,87,.4)}50%{box-shadow:0 0 0 11px rgba(255,61,87,0)}}
  @keyframes fbWobble{0%,100%{transform:translate(-50%,-50%) rotate(-5deg)}50%{transform:translate(-50%,-50%) rotate(5deg)}}

  /* ── 3D slab order card ── */
  .oc{
    background:${F.panelBg};border:3px solid ${F.dark};border-radius:24px;overflow:hidden;
    box-shadow:0 7px 0 ${F.dark},0 16px 30px rgba(43,26,18,.14);
    transition:transform .16s ease,box-shadow .16s ease;
    animation:fbRise .38s ease both;margin-bottom:18px;
  }
  .oc:hover{transform:translateY(-4px);box-shadow:0 11px 0 ${F.dark},0 22px 38px rgba(43,26,18,.18);}
  .oc.open{box-shadow:0 7px 0 ${F.red},0 18px 34px rgba(255,61,87,.24);}
  .oc-head{display:flex;align-items:stretch;cursor:pointer;background:${F.panelBg};}
  .oc-head:active{transform:translateY(2px);}

  /* ── domed item tile ── */
  .it{
    border:2.5px solid ${F.dark};border-radius:18px;overflow:hidden;background:${F.panelBg};
    box-shadow:0 4px 0 ${F.dark};transition:transform .16s ease,box-shadow .16s ease;
    display:flex;flex-direction:column;
  }
  .it:hover{transform:translateY(-3px);box-shadow:0 7px 0 ${F.dark},0 12px 20px rgba(43,26,18,.16);}
  .it:active{transform:translateY(3px);box-shadow:0 1px 0 ${F.dark};}
  .it-media{position:relative;width:100%;aspect-ratio:1/1;background:${F.orangeSoft};cursor:pointer;}
  .it-dome-tl{
    position:absolute;top:0;left:0;padding:6px 12px;border-bottom-right-radius:26px;z-index:3;
    background:linear-gradient(135deg,rgba(43,26,18,.9),${F.red});
    color:#fff;font-size:10.5px;font-weight:800;display:flex;align-items:center;gap:4px;
    max-width:88%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .it-dome-br{
    position:absolute;bottom:0;right:0;padding:7px 13px;border-top-left-radius:26px;z-index:3;
    background:linear-gradient(135deg,#4A3220,${F.dark} 55%,${F.orangeDark});
    color:#fff;font-size:12.5px;font-weight:900;white-space:nowrap;
    font-family:'Baloo 2',sans-serif;letter-spacing:-.3px;
  }
  .it-pill{
    position:absolute;bottom:7px;left:7px;padding:4px 10px;border-radius:999px;z-index:3;
    background:linear-gradient(135deg,${F.orangeSoft},#FFB347,${F.orange});
    color:${F.dark};font-size:10px;font-weight:800;
    max-width:70%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .it-diet{
    position:absolute;top:8px;right:8px;z-index:3;display:flex;flex-direction:column;gap:5px;align-items:flex-end;
  }
  .it-diet i{
    display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;
    border:2px solid ${F.dark};background:${F.panelBg};box-shadow:0 2px 0 ${F.dark};
  }
  .it-foot{padding:9px 11px 11px;border-top:2.5px solid ${F.dark};background:${F.bg};}

  /* ── buttons ── */
  .btn{
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:8px 15px;border-radius:999px;font-size:12px;font-weight:800;
    font-family:'Baloo 2',sans-serif;white-space:nowrap;
    border:2.5px solid ${F.dark};background:${F.panelBg};color:${F.dark};
    box-shadow:0 4px 0 ${F.dark};transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .btn:hover{transform:translateY(-2px);box-shadow:0 6px 0 ${F.dark};}
  .btn:active{transform:translateY(3px);box-shadow:0 1px 0 ${F.dark};}
  .btn.gr{background:${GRAD_RED};color:#fff;}
  .btn.gd{background:${F.orangeSoft};color:${F.dark};}
  .btn.gn{background:${F.greenSoft};color:${F.greenDark};}
  .btn.rd{background:${F.redSoft};color:${F.redDark};}
  .btn.sm{padding:5px 11px;font-size:10.5px;border-width:2px;box-shadow:0 3px 0 ${F.dark};}
  .btn.sm:hover{box-shadow:0 5px 0 ${F.dark};}
  .btn.sm:active{box-shadow:0 1px 0 ${F.dark};}

  /* ── slim inline filter pills ── */
  .fc{
    display:inline-flex;align-items:center;gap:6px;flex-shrink:0;
    padding:7px 13px;border-radius:999px;border:2px solid ${F.dark};
    background:${F.panelBg};color:${F.dark};box-shadow:0 3px 0 ${F.dark};
    font-family:'Baloo 2',sans-serif;font-size:11.5px;font-weight:800;
    transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .fc:hover{transform:translateY(-2px);box-shadow:0 5px 0 ${F.dark};background:${F.orangeSoft};}
  .fc:active{transform:translateY(2px);box-shadow:0 1px 0 ${F.dark};}
  .fc.on{background:${GRAD_RED};color:#fff;border-color:${F.dark};}
  .fc-n{
    display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;
    padding:0 5px;border-radius:999px;font-size:10.5px;font-weight:900;
    background:${F.orangeSoft};color:${F.dark};
  }
  .fc.on .fc-n{background:rgba(255,255,255,.92);color:${F.redDark};}

  /* ── search ── */
  .sw{
    display:flex;align-items:center;flex:1;background:${F.panelBg};border-radius:14px;
    overflow:hidden;border:2px solid ${F.dark};box-shadow:0 3px 0 ${F.dark};
    transition:box-shadow .16s ease;
  }
  .sw:focus-within{box-shadow:0 3px 0 ${F.red};}

  .tab{
    display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:800;
    color:${F.textFaint};padding:4px 10px;border:none;background:none;transition:color .18s;
    font-family:'Montserrat',sans-serif;
  }
  .tab.on{color:${F.red};}

  /* ── the delivery road ── */
  .road-wrap{position:relative;width:100%;max-width:340px;margin:0 auto;}
  .road-svg{position:absolute;inset:0;width:100%;height:100%;overflow:visible;}
  .road-dash{animation:fbDash 1.1s linear infinite;}
  .road-stop{
    position:absolute;width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;
    transform:translate(-50%,-50%);border:2.5px solid ${F.dark};z-index:2;transition:background .25s;
  }
  .road-stop.done{background:${GRAD_RED};box-shadow:0 3px 0 ${F.dark};}
  .road-stop.now{background:${GRAD_RED};box-shadow:0 3px 0 ${F.dark};animation:fbPulse 1.8s ease-in-out infinite;}
  .road-stop.todo{background:${F.panelBg};box-shadow:0 3px 0 ${F.dark};}
  .road-stop.halt{background:${F.divider};box-shadow:0 3px 0 ${F.dark};}
  .road-label{position:absolute;transform:translateY(-50%);z-index:2;max-width:116px;}
  .road-label b{display:block;font-size:11.5px;font-weight:800;color:${F.dark};font-family:'Baloo 2',sans-serif;line-height:1.2;}
  .road-label span{display:block;font-size:9.5px;color:${F.textMuted};margin-top:2px;line-height:1.3;font-weight:600;}
  .road-label.pending b{color:${F.textFaint};}
  .road-van{
    position:absolute;width:30px;height:30px;border-radius:50%;background:${F.panelBg};
    border:2.5px solid ${F.dark};display:flex;align-items:center;justify-content:center;z-index:3;
    box-shadow:0 3px 0 ${F.dark};animation:fbWobble 2.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce){
    .oc,.it,.btn,.fc,.road-dash,.road-stop.now,.road-van{animation:none!important;transition:none!important;}
  }
  @media (max-width: 520px){ .items-grid{grid-template-columns:1fr 1fr!important;} }
`;

/* ─── helpers ─── */
const fmt = (n?: number) => (!n || isNaN(n) ? "0.00" : Number(n).toFixed(2));
const fmtD = (ts: any) => {
  if (!ts) return "—";
  const ms = ts?.seconds ? ts.seconds * 1000 : ts;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Sizes arrive as an array from the seller screen, a keyed map from older docs. */
interface PortionRef { id: string; name: string; unit?: string }
interface ExtraRef { id: string; name: string }

const portionsOf = (listing: any): PortionRef[] => {
  const raw = listing?.portions;
  if (!raw) return [];
  return Array.isArray(raw)
    ? raw.map((p: any) => ({ id: String(p?.id ?? ""), name: p?.name ?? "", unit: p?.unit ?? "" }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({ id: k, name: v?.name ?? k, unit: v?.unit ?? "" }));
};
const extrasOf = (listing: any): ExtraRef[] =>
  Array.isArray(listing?.extras)
    ? listing.extras.map((e: any) => ({ id: String(e?.id ?? ""), name: e?.name ?? "" }))
    : [];

type SK = "Confirmed" | "Preparing" | "On the way" | "Delivered" | "Cancelled" | "Refunded";

const SCFG: Record<SK, { bg: string; tx: string; Icon: any }> = {
  Confirmed:    { bg: F.orangeSoft, tx: F.orangeDark, Icon: ReceiptText  },
  Preparing:    { bg: F.redSoft,    tx: F.redDark,    Icon: Package      },
  "On the way": { bg: F.orangeSoft, tx: F.red,        Icon: Truck        },
  Delivered:    { bg: F.greenSoft,  tx: F.greenDark,  Icon: CheckCircle2 },
  Cancelled:    { bg: F.divider,    tx: F.textMuted,  Icon: Ban          },
  Refunded:     { bg: F.orangeSoft, tx: F.dark,       Icon: RotateCcw    },
};

const statusOf = (o: FoodOrder): SK => {
  if (o.isCancelled) return "Cancelled";
  if (o.isRefunded) return "Refunded";
  if (o.deliveryStatus?.buyerReceived || o.isDelivered) return "Delivered";
  if (o.deliveryStatus?.orderDelivered) return "On the way";
  if (o.deliveryStatus?.sellerDelivered || o.isPaid) return "Preparing";
  return "Confirmed";
};

/* ═══════════════════════════════════════════════
   SHIPMENT STATE — the food twin of the store's
   brand-row read. Food orders have no brands[]: the
   flags live on each line in items[], and every line
   carries the hostId of the kitchen that cooks it.
   So "a parcel" here is one host's set of lines.
   markFoodHostItemsDelivered rejects an empty list,
   so when nothing resolves we hide the button rather
   than fire a call that throws.
═══════════════════════════════════════════════ */
/* the food twin of the store's brand-row read. Food items carry
   brandId directly on each line (same field the ItemTile already
   uses), so grouping/keying works exactly like the store version —
   only the underlying flags live per-line instead of in order.brands[] */
const brandIdOf = (i: any) => String(i?.brandId ?? i?.hostId ?? "");

const shipmentState = (o: FoodOrder) => {
  const lines: any[] = Array.isArray(o.items) ? o.items.filter((i: any) => brandIdOf(i)) : [];
  const ds = (i: any) => i?.deliveryStatus ?? o.deliveryStatus ?? {};

  const brandIds = [...new Set(lines.map((i) => brandIdOf(i)))];
  const shipped = brandIds.filter((b) =>
    lines.filter((i) => brandIdOf(i) === b).every((i) => ds(i).sellerDelivered === true)
  );
  const confirmed = brandIds.filter((b) =>
    lines.filter((i) => brandIdOf(i) === b).every((i) => ds(i).buyerReceived === true)
  );
  const pendingIds = shipped.filter((b) => !confirmed.includes(b));

  return {
    hasBrands: brandIds.length > 0,
    total: brandIds.length,
    shippedCount: shipped.length,
    confirmedCount: confirmed.length,
    pendingIds,
  };
};

/* items grouped by brand, in first-seen order — one section per seller */
const groupItemsByBrand = (items: any[]) => {
  const map = new Map<string, any[]>();
  const order: string[] = [];
  items.forEach((item: any) => {
    const bId = brandIdOf(item) || "unknown";
    if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
    map.get(bId)!.push(item);
  });
  return order.map((brandId) => ({ brandId, items: map.get(brandId)! }));
};

/* per-brand shipment flags, read straight off that brand's lines */
const brandDeliveryInfo = (o: FoodOrder, brandId: string) => {
  const lines: any[] = Array.isArray(o.items)
    ? o.items.filter((i: any) => brandIdOf(i) === brandId)
    : [];
  const ds = (i: any) => i?.deliveryStatus ?? o.deliveryStatus ?? {};
  const hasEntry = lines.length > 0;
  const sellerDelivered = hasEntry && lines.every((i) => ds(i).sellerDelivered === true);
  const buyerReceived = hasEntry && lines.every((i) => ds(i).buyerReceived === true);
  const brandName = lines[0]?.brandName || lines[0]?.hostName || lines[0]?.vendor || null;
  return { hasEntry, sellerDelivered, buyerReceived, brandName };
};

const Badge: React.FC<{ s: SK }> = ({ s }) => {
  const c = SCFG[s];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4, background: c.bg,
      border: `2px solid ${F.dark}`, borderRadius: 999, padding: "3px 10px",
      boxShadow: `0 3px 0 ${F.dark}`, flexShrink: 0,
    }}>
      <c.Icon size={10} color={c.tx} strokeWidth={2.4} />
      <span style={{ fontSize: 10, fontWeight: 800, color: c.tx, fontFamily: "'Baloo 2',sans-serif" }}>{s}</span>
    </div>
  );
};

/* ── photo with a drawn placeholder, no logo stamp ── */
const Photo: React.FC<{ src?: string | null; alt: string }> = ({ src, alt }) => {
  const [dead, setDead] = useState(false);
  if (src && !dead) {
    return (
      <img src={src} alt={alt} loading="lazy" onError={() => setDead(true)}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    );
  }
  return (
    <div style={{
      width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
      background: `repeating-linear-gradient(45deg,${F.orangeSoft} 0 10px,${F.divider} 10px 20px)`,
    }}>
      <Sprout size={22} color={F.textFaint} strokeWidth={1.8} />
    </div>
  );
};

const FilterPill: React.FC<{
  icon: React.ElementType; label: string; value: number; active: boolean; onClick: () => void;
}> = ({ icon: Icon, label, value, active, onClick }) => (
  <button className={`fc${active ? " on" : ""}`} onClick={onClick}>
    <Icon size={12} color={active ? "#fff" : F.textMuted} strokeWidth={2.2} />
    {label}
    <span className="fc-n">{value}</span>
  </button>
);

/* ═══════════════════════════════════════════════
   THE ROAD — five stops from the till to the doorstep.
   One continuous serpentine; the coloured overlay is
   clipped with pathLength so the travelled stretch is
   exact rather than eyeballed.
═══════════════════════════════════════════════ */
const STOPS = [
  { key: "confirmed", label: "Order confirmed", hint: "We've got your basket", Icon: ReceiptText },
  { key: "paid",      label: "Payment accepted", hint: "Cleared and sent on",  Icon: CreditCard  },
  { key: "packed",    label: "Grower packing",   hint: "Picked and boxed",     Icon: Sprout      },
  { key: "road",      label: "On the road",      hint: "Out for delivery",     Icon: Truck       },
  { key: "home",      label: "Delivered",        hint: "Eat it fresh",         Icon: PartyPopper },
];

const reachedIndex = (o: FoodOrder): number => {
  if (o.deliveryStatus?.buyerReceived || o.isDelivered) return 4;
  if (o.deliveryStatus?.orderDelivered) return 3;
  if (o.deliveryStatus?.sellerDelivered) return 2;
  if (o.isPaid) return 1;
  return 0;
};

const VB_W = 300;
const STOP_GAP = 78;
const PAD_Y = 42;
const VB_H = PAD_Y * 2 + STOP_GAP * (STOPS.length - 1);

const stopPoints = STOPS.map((_, i) => ({
  x: i % 2 === 0 ? 58 : VB_W - 58,
  y: PAD_Y + i * STOP_GAP,
}));

const roadPath = stopPoints.reduce((d, p, i) => {
  if (i === 0) return `M ${p.x} ${p.y}`;
  const prev = stopPoints[i - 1];
  const dy = (p.y - prev.y) / 2;
  return `${d} C ${prev.x} ${prev.y + dy}, ${p.x} ${p.y - dy}, ${p.x} ${p.y}`;
}, "");

const DeliveryRoad: React.FC<{ order: FoodOrder }> = ({ order }) => {
  const halted = order.isCancelled || order.isRefunded;
  const reached = reachedIndex(order);
  const travelled = halted ? 0 : (reached / (STOPS.length - 1)) * 100;
  const gradId = `fbRoad-${order.id}`;

  return (
    <div className="road-wrap" style={{ aspectRatio: `${VB_W} / ${VB_H}` }}>
      <svg className="road-svg" viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFB347" />
            <stop offset="55%" stopColor={F.orange} />
            <stop offset="100%" stopColor={F.red} />
          </linearGradient>
        </defs>

        {/* asphalt — drawn twice so the road gets the same ink outline as everything else */}
        <path d={roadPath} fill="none" stroke={F.dark} strokeWidth={26} strokeLinecap="round" />
        <path d={roadPath} fill="none" stroke={F.divider} strokeWidth={20} strokeLinecap="round" />
        {travelled > 0 && (
          <path d={roadPath} pathLength={100} fill="none" stroke={`url(#${gradId})`} strokeWidth={20}
            strokeLinecap="round" strokeDasharray={`${travelled} 100`}
            style={{ transition: "stroke-dasharray .6s ease" }} />
        )}
        <path className={halted ? undefined : "road-dash"} d={roadPath} fill="none" stroke="#fff" strokeWidth={2.5}
          strokeLinecap="round" strokeDasharray="10 18" opacity={0.8} />
      </svg>

      {STOPS.map((s, i) => {
        const p = stopPoints[i];
        const leftSide = i % 2 === 0;
        const state = halted ? (i <= reached ? "halt" : "todo") : i < reached ? "done" : i === reached ? "now" : "todo";
        const active = state === "done" || state === "now";
        return (
          <React.Fragment key={s.key}>
            <div className={`road-stop ${state}`}
              style={{ left: `${(p.x / VB_W) * 100}%`, top: `${(p.y / VB_H) * 100}%` }}>
              <s.Icon size={18} color={active ? "#fff" : F.textFaint} strokeWidth={2.2} />
            </div>
            <div className={`road-label${active ? "" : " pending"}`}
              style={{
                top: `${(p.y / VB_H) * 100}%`,
                ...(leftSide
                  ? { left: `${((p.x + 32) / VB_W) * 100}%`, textAlign: "left" as const }
                  : { right: `${((VB_W - p.x + 32) / VB_W) * 100}%`, textAlign: "right" as const }),
              }}>
              <b>{s.label}</b>
              <span>{halted && i > reached ? "Not reached" : s.hint}</span>
            </div>
          </React.Fragment>
        );
      })}

      {!halted && reached < STOPS.length - 1 && (
        <div className="road-van" style={{
          left: `${((stopPoints[reached].x + (reached % 2 === 0 ? 30 : -30)) / VB_W) * 100}%`,
          top: `${((stopPoints[reached].y - 30) / VB_H) * 100}%`,
        }}>
          <Truck size={14} color={F.orange} strokeWidth={2.4} />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ITEM TILE — square photo, corner domes, portion
   pill, diet dots, name + actions in a footer strip.
═══════════════════════════════════════════════ */
const ItemTile: React.FC<{
  item: any; order: FoodOrder; listingsMap: Record<string, any>; onNav: (p: string) => void; index: number;
}> = ({ item, order, listingsMap, onNav, index }) => {
  const p = listingsMap[item.listingId] ?? {};
  const name = p?.name || item.name || "Item";
  const line = (item.rate ?? p?.rate ?? 0) * (item.qty ?? 1);
  const brandId = item.brandId ?? p?.brandId ?? item.hostId;
  const delivered = !!order.isDelivered;

  const size = item.portionId ? portionsOf(p).find((x) => x.id === item.portionId) : null;
  const extraNames = (item.extraIds ?? [])
    .map((id: string) => extrasOf(p).find((e) => e.id === id)?.name)
    .filter(Boolean) as string[];
  const tag = [size ? `${size.name}${size.unit ? ` · ${size.unit}` : ""}` : null, ...extraNames]
    .filter(Boolean).slice(0, 2).join(" · ");

  const go = () => brandId && onNav(`/FreshStore/${brandId}`);

  return (
    <div className="it" style={{ animation: "fbRise .4s ease both", animationDelay: `${index * 55}ms` }}>
      <div className="it-media" onClick={go} role="link" tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && go()}
        aria-label={`Open the grower page for ${name}`}>
        <Photo src={p?.photos?.[0] || item.photo || item.image} alt={name} />

        <div className="it-dome-tl">×{item.qty ?? 1}</div>

        <div className="it-diet">
          {p?.isOrganic && <i title="Organic"><Leaf size={11} color={F.green} strokeWidth={2.6} /></i>}
          {p?.isGlutenFree && <i title="Gluten free"><WheatOff size={11} color={F.orangeDark} strokeWidth={2.6} /></i>}
          {(p?.spiceLevel ?? 0) > 0 && <i title="Spicy"><Flame size={11} color={F.red} strokeWidth={2.6} /></i>}
        </div>

        {tag && <span className="it-pill">{tag}</span>}
        <div className="it-dome-br">€{fmt(line)}</div>
      </div>

      <div className="it-foot">
        <div title={name} style={{
          fontSize: 12.5, fontWeight: 700, color: F.dark, fontFamily: "'Baloo 2',sans-serif",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 8,
        }}>{name}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn sm gd" onClick={go}><RotateCcw size={10} /> Buy again</button>
          {delivered && (
            <button className="btn sm gn"
              onClick={() => onNav(`/freshproductreview/${order.guestId}/${item.listingId}/${order.id}`)}>
              <Star size={10} /> Review
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ORDER CARD — slab rectangle, taps open to reveal
   the road, the basket grid and the payment summary.
═══════════════════════════════════════════════ */
const OrderCard: React.FC<{
  order: FoodOrder; listingsMap: Record<string, any>;
  onNav: (p: string) => void; onSupport: () => void;
  onConfirm: (o: FoodOrder, brandIds: string[]) => void;
  delay: number;
}> = ({ order, listingsMap, onNav, onSupport, onConfirm, delay }) => {
  const [open, setOpen] = useState(false);
  const items = order.items || [];
  const fi: any = items[0];
  const prod = listingsMap[fi?.listingId] ?? {};
  const st = statusOf(order);
  const cnt = items.length;
  const ordId = (order.paymentId || order.id || "").slice(0, 14).toUpperCase();
  const stopped = !!(order.isCancelled || order.isRefunded);
  const received = !!(order.deliveryStatus?.buyerReceived || order.isDelivered);
  const live = !received && !stopped;
  const ship = shipmentState(order);
  const brandGroups = useMemo(() => groupItemsByBrand(items), [items]);
  

  return (
    <motion.div layout className={`oc${open ? " open" : ""}`} style={{ animationDelay: `${delay * 55}ms` }}>
      <div style={{ height: 4, background: SWEEP, backgroundSize: "300% 100%", animation: "fbSpin 4s linear infinite" }} />

      <div className="oc-head" onClick={() => setOpen((v) => !v)} role="button" tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)}>
        <div style={{ width: 104, height: 104, flexShrink: 0, borderRight: `3px solid ${F.dark}`, overflow: "hidden" }}>
          <Photo src={prod?.photos?.[0] || fi?.photo || fi?.image} alt={prod?.name || fi?.name || "Order"} />
        </div>

        <div style={{ flex: 1, padding: "12px 15px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 800, color: F.dark, fontFamily: "'Baloo 2',sans-serif",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: -.2,
              }}>
                {prod?.name || fi?.name || "Order"}
                {cnt > 1 && <span style={{ fontSize: 11, color: F.textMuted, fontWeight: 600, marginLeft: 6 }}>+{cnt - 1} more</span>}
              </div>
              <div style={{ fontSize: 10, color: F.textMuted, marginTop: 4, fontWeight: 600 }}>
                #{ordId} · {fmtD(order.createdAt)} · {cnt} item{cnt > 1 ? "s" : ""}
              </div>
            </div>
            <Badge s={st} />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: F.red, fontFamily: "'Baloo 2',sans-serif", letterSpacing: -.5 }}>
              €{fmt(order.totalPaid)}
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 800,
              color: F.textMuted, letterSpacing: ".05em", textTransform: "uppercase",
            }}>
              {open ? "Hide basket" : "See basket"}
              <ChevronRight size={15} color={F.red}
                style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .22s" }} />
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: .28, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: 18, borderTop: `3px solid ${F.dark}`, background: F.bg }}>

              {/* ── the road ── */}
              <p style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".2em", color: F.textMuted, marginBottom: 10 }}>
                {stopped ? "JOURNEY STOPPED" : "ON ITS WAY TO YOU"}
              </p>
              {stopped && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, background: F.redSoft,
                  border: `2.5px solid ${F.dark}`, borderRadius: 16, padding: "10px 13px",
                  marginBottom: 12, boxShadow: `0 4px 0 ${F.dark}`,
                }}>
                  <Ban size={13} color={F.redDark} strokeWidth={2.4} />
                  <span style={{ fontSize: 11.5, color: F.redDark, fontWeight: 800 }}>
                    {order.isRefunded ? "This order was refunded." : "This order was cancelled."}
                  </span>
                </div>
              )}
              <div style={{
                background: F.panelBg, border: `2.5px solid ${F.dark}`, borderRadius: 18,
                padding: "14px 10px 10px", marginBottom: 18, boxShadow: `0 5px 0 ${F.dark}`,
              }}>
                <DeliveryRoad order={order} />
              </div>

              {/* ── basket, grouped by kitchen ── */}
              <p style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".2em", color: F.textMuted, marginBottom: 12 }}>
                WHAT'S IN THIS BASKET
              </p>

              {brandGroups.map((group, gi) => {
                const info = brandDeliveryInfo(order, group.brandId);
                const firstItem: any = group.items[0];
                const brandName = info.brandName || listingsMap[firstItem?.listingId]?.vendor || "Grower";
                const pending = live && info.sellerDelivered && !info.buyerReceived;
                const notShippedYet = live && info.hasEntry && !info.sellerDelivered;
                const alreadyConfirmed = info.buyerReceived;

                return (
                  <div key={group.brandId} style={{ marginBottom: gi < brandGroups.length - 1 ? 22 : 18 }}>
                    {/* ── brand header bar: name left, per-brand confirm button far right ── */}
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                      background: F.panelBg, border: `2.5px solid ${F.dark}`, borderRadius: 14,
                      padding: "10px 14px", boxShadow: `0 4px 0 ${F.dark}`, marginBottom: 12,
                    }}>
                      <span style={{
                        fontSize: 13, fontWeight: 800, color: F.dark, fontFamily: "'Baloo 2',sans-serif",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                      }}>
                        {brandName}
                      </span>

                      {pending ? (
                        <button className="btn sm gr" onClick={() => onConfirm(order, [group.brandId])} style={{ flexShrink: 0 }}>
                          <PackageCheck size={11} /> Confirm arrived
                        </button>
                      ) : notShippedYet ? (
                        <span className="btn sm gd" style={{ cursor: "default", flexShrink: 0 }}>
                          <Truck size={11} /> Still with grower
                        </span>
                      ) : alreadyConfirmed ? (
                        <span className="btn sm gn" style={{ cursor: "default", flexShrink: 0 }}>
                          <CheckCircle2 size={11} /> Arrived
                        </span>
                      ) : null}
                    </div>

                    {/* ── that brand's items ── */}
                    <div className="items-grid" style={{
                      display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 14,
                    }}>
                      {group.items.map((item: any, i: number) => (
                        <ItemTile key={(item.listingId || item.id || "i") + "-" + i} item={item} order={order}
                          listingsMap={listingsMap} onNav={onNav} index={i} />
                      ))}
                    </div>

                    {/* ── sweep bar between brand sections ── */}
                    {gi < brandGroups.length - 1 && (
                      <div style={{
                        height: 7, borderRadius: 999, marginTop: 20,
                        background: SWEEP, backgroundSize: "300% 100%", animation: "fbSpin 4s linear infinite",
                        border: `2px solid ${F.dark}`, boxShadow: `0 3px 0 ${F.dark}`,
                      }} />
                    )}
                  </div>
                );
              })}

              {/* ── payment summary ── */}
              <div style={{
                marginTop: 18, background: F.panelBg, border: `2.5px solid ${F.dark}`, borderRadius: 18,
                padding: "14px 16px", boxShadow: `0 5px 0 ${F.dark}`,
              }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".18em", color: F.textMuted, marginBottom: 10 }}>
                  PAYMENT SUMMARY
                </div>
                {[
                  { l: "Subtotal",    v: `€${fmt(order.subtotal)}` },
                  { l: "Service fee", v: `€${fmt(order.guestsFee)}` },
                  { l: "Delivery",    v: order.deliveryTotal ? `€${fmt(order.deliveryTotal)}` : "Free" },
                ].map(({ l, v }, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${F.divider}` }}>
                    <span style={{ fontSize: 12, color: F.textMuted }}>{l}</span>
                    <span style={{ fontSize: 12, color: F.dark, fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: F.dark, fontFamily: "'Baloo 2',sans-serif" }}>Total paid</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: F.red, fontFamily: "'Baloo 2',sans-serif", letterSpacing: -.4 }}>
                    €{fmt(order.totalPaid)}
                  </span>
                </div>
                {order.customer?.address && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${F.divider}` }}>
                    <MapPin size={12} color={F.textFaint} style={{ marginTop: 1, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: F.textMuted, lineHeight: 1.5, fontWeight: 600 }}>
                      {[order.customer.address, order.customer.city, order.customer.country].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
              </div>

              {/* ── order-level actions ── */}
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
                <button className="btn gd" onClick={() => onNav(`/FreshStore/${order.brandId ?? fi?.brandId}`)}>
                  <Eye size={12} /> Visit grower
                </button>

                {ship.total > 1 && (
                  <span style={{ fontSize: 10.5, color: F.textMuted, fontWeight: 600 }}>
                    {ship.confirmedCount} of {ship.total} kitchens confirmed
                  </span>
                )}

                {live && (
                  <>
                    <button className="btn rd" onClick={onSupport}>
                      <Ban size={12} /> Cancel order
                    </button>
                    <span style={{ fontSize: 10.5, color: F.textMuted, fontWeight: 600 }}>
                      Support handles cancellations.
                    </span>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
const FreshBuyerOrders: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const guestId = userId ?? authUser?.userId ?? "";

  const orders = useSelector((s: RootState) => (s as any).FoodOrder?.guestOrders ?? []);
  const listings = useSelector((s: RootState) => (s as any).food?.listings ?? []);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | SK>("all");
  const [isMob, setIsMob] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!guestId) return;
    const unsub = dispatch(listenToFoodGuestOrders(guestId) as any);
    dispatch(listenToPublishedFoods() as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [guestId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    (listings as any[]).forEach((l: any) => { m[l.listingId] = l; });
    return m;
  }, [listings]);

  const sorted = useMemo(
    () => [...(orders as FoodOrder[])].sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)),
    [orders]
  );

  const cnt = (s: SK) => sorted.filter((o) => statusOf(o) === s).length;
  const confirmedN = cnt("Confirmed");
  const preparingN = cnt("Preparing");
  const onTheWayN = cnt("On the way");
  const deliveredN = cnt("Delivered");
  const cancelledN = cnt("Cancelled");
  const activeN = confirmedN + preparingN + onTheWayN;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter((o) => {
      const status = statusOf(o);
      if (filter === "all") {
        if (status === "Delivered" || status === "Cancelled" || status === "Refunded") return false;
      } else if (status !== filter) {
        return false;
      }
      if (!q) return true;
      const fi: any = o.items?.[0];
      const p = listingsMap[fi?.listingId] ?? {};
      return `${p?.name ?? ""} ${o.id ?? ""} ${fi?.name ?? ""}`.toLowerCase().includes(q);
    });
  }, [sorted, filter, search, listingsMap]);

  const goToSupport = () => navigate(supportRoute(guestId));

  /* buyer half of the seller's own thunk; role:"buyer" stamps
     only buyerReceived, so an unshipped brand can't be closed out early */
  const onConfirm = (o: FoodOrder, brandIds: string[]) => {
    if (!brandIds.length) return;
    dispatch(markFoodItemsDelivered({ orderId: o.id, brandIds, role: "buyer" }) as any);
  };

  

  /* ── nothing ordered yet ── */
  if (sorted.length === 0) return (
    <div style={{ minHeight: "100vh", background: F.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Montserrat',sans-serif" }}>
      <style>{CSS}</style>
      <div style={{
        textAlign: "center", maxWidth: 380, margin: "0 20px", background: F.panelBg,
        border: `3px solid ${F.dark}`, borderRadius: 28, padding: "40px 30px",
        boxShadow: `0 8px 0 ${F.dark},0 22px 44px rgba(43,26,18,.16)`,
      }}>
        <div style={{ fontSize: 58, marginBottom: 16, animation: "fbBob 3.2s ease-in-out infinite" }}>🧺</div>
        <h2 style={{ fontWeight: 900, fontSize: 26, color: F.dark, marginBottom: 8, fontFamily: "'Baloo 2',sans-serif", letterSpacing: -.5 }}>
          No baskets yet
        </h2>
        <p style={{ color: F.textMuted, fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
          Every order you place shows up here — road, receipts and all.
        </p>
        <button className="btn gr" style={{ fontSize: 13.5, padding: "13px 28px" }} onClick={() => navigate(`/FreshStoreHome/${guestId}`)}>
          <Sprout size={14} /> Start shopping <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: F.bg, fontFamily: "'Montserrat',sans-serif", paddingBottom: isMob ? 90 : 40 }}>
      <style>{CSS}</style>

      {/* ══ HEADER — title and search share one row, pills sit under ══ */}
      <div style={{
        background: "rgba(255,248,241,.94)", backdropFilter: "blur(14px)",
        borderBottom: `2px solid ${F.dark}`, position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ height: 3, background: SWEEP, backgroundSize: "300% 100%", animation: "fbSpin 4s linear infinite" }} />
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "10px 20px 9px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontWeight: 900, fontSize: isMob ? 19 : 22, color: F.dark, letterSpacing: -.5, lineHeight: 1.1, fontFamily: "'Baloo 2',sans-serif" }}>
              My orders
            </h1>
            {activeN > 0 && (
              <p style={{ fontSize: 11, color: F.textMuted, fontWeight: 600, marginTop: 1 }}>{activeN} on the road</p>
            )}
          </div>

          <div className="sw" style={{ marginLeft: "auto", maxWidth: 340 }}>
            <div style={{ padding: "0 11px" }}><Search size={13} color={F.textFaint} /></div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders"
              style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 12.5, fontWeight: 600, color: F.dark, padding: "8px 11px 8px 0" }} />
          </div>
        </div>

        <div style={{ maxWidth: 780, margin: "0 auto", display: "flex", gap: 8, overflowX: "auto", padding: "0 20px 10px", scrollbarWidth: "none" as any }}>
          <FilterPill icon={ShoppingBasket} label="Active"     value={activeN}     active={filter === "all"}         onClick={() => setFilter("all")} />
          <FilterPill icon={ReceiptText}    label="Confirmed"  value={confirmedN}  active={filter === "Confirmed"}   onClick={() => setFilter("Confirmed")} />
          <FilterPill icon={Package}        label="Preparing"  value={preparingN}  active={filter === "Preparing"}   onClick={() => setFilter("Preparing")} />
          <FilterPill icon={Truck}          label="On the way" value={onTheWayN}   active={filter === "On the way"}  onClick={() => setFilter("On the way")} />
          <FilterPill icon={CheckCircle2}   label="Delivered"  value={deliveredN}  active={filter === "Delivered"}   onClick={() => setFilter("Delivered")} />
          <FilterPill icon={Ban}            label="Cancelled"  value={cancelledN}  active={filter === "Cancelled"}   onClick={() => setFilter("Cancelled")} />
        </div>
      </div>

      {/* ══ ORDER LIST ══ */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: isMob ? "20px 14px" : "26px 20px" }}>
        {filtered.length === 0 ? (
          <div style={{
            textAlign: "center", background: F.panelBg, border: `3px solid ${F.dark}`, borderRadius: 26,
            padding: "44px 28px", boxShadow: `0 7px 0 ${F.dark}`,
          }}>
            <div style={{ fontSize: 46, marginBottom: 14, animation: "fbWiggle 2.6s ease-in-out infinite" }}>🥬</div>
            <h3 style={{ fontWeight: 900, fontSize: 19, color: F.dark, marginBottom: 8, fontFamily: "'Baloo 2',sans-serif" }}>Nothing on this road</h3>
            <p style={{ color: F.textMuted, fontSize: 13, marginBottom: 22, fontWeight: 600 }}>
              Clear the search or pick another filter above.
            </p>
            <button className="btn gr" onClick={() => { setSearch(""); setFilter("all"); }}>
              <RotateCcw size={12} /> Reset filters
            </button>
          </div>
        ) : (
          <>
            {filtered.map((order, i) => (
              <OrderCard key={order.id} order={order} listingsMap={listingsMap}
                onNav={(path) => navigate(path)} onSupport={goToSupport}
                onConfirm={onConfirm} delay={i} />
            ))}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
              <button className="btn gd" onClick={goToSupport}>
                <LifeBuoy size={13} /> Something wrong with an order?
              </button>
            </div>
          </>
        )}
      </div>

      {/* ══ MOBILE TABS ══ */}
     
    </div>
  );
};

export default FreshBuyerOrders;